import { useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { PayloadStates } from '@lore/utils';
import { useConfig } from '@lore/config';
import * as Sentry from '@sentry/browser';

// TODO: Fetching a capped number of large pages is a stopgap. Replace with server-side aggregation.
export const PAGE_SIZE = 1000;
export const MAX_PAGES = 4; // up to 4,000 rows per query
export const DEFAULT_MESSAGE = 'Paged query exceeded the page limit';

const STALE_TIME = 5 * 60 * 1000; // 5 minutes, matches the large page cache rule in useQueryConnect

/**
 * Build the query string the same way the Lore `find` blueprint does: object values are JSON encoded.
 *
 * @param {Object} where
 * @param {{page: number, pageSize: number}} pagination
 * @returns {URLSearchParams}
 */
export function toSearchParams(where = {}, pagination = {}) {
  const searchParams = new URLSearchParams();
  Object.entries({ ...where, ...pagination }).forEach(([name, value]) => {
    if (value === undefined) {
      return;
    }
    searchParams.append(name, value !== null && typeof value === 'object' ? JSON.stringify(value) : String(value));
  });
  return searchParams;
}

/**
 * Fetch up to `maxPages` pages of a steward-api list endpoint and merge them into a single payload.
 *
 * Page 1 is always requested. Additional pages are requested only once page 1 has
 * resolved and its `count` shows they are needed. The number of queries is fixed
 * (`maxPages`).
 *
 * @param {string} resource - API endpoint relative to the default `apiRoot`, e.g. 'projects'
 * @param {Object} params - query params (`where`); `pagination` is managed here
 * @param {Object} [options]
 * @param {string} [options.source] - name used in the Sentry warning when truncated
 * @param {string} [options.message] - Sentry warning message used when truncated; `(source)` is appended
 * @param {number} [options.pageSize]
 * @param {number} [options.maxPages]
 * @returns {{state: string, data: Object[], meta: {totalCount: number, pagesFetched: number, truncated: boolean}}}
 */
export default function usePagedQuery(resource, params = {}, {
  source,
  message = DEFAULT_MESSAGE,
  pageSize = PAGE_SIZE,
  maxPages = MAX_PAGES
} = {}) {
  const config = useConfig();
  const { apiRoot, headers } = config.connections.default;
  const where = params.where || {};

  function buildQuery(page, enabled) {
    return {
      queryKey: [resource, 'pages', { where, page, pageSize }],
      queryFn: () => {
        const url = new URL(`${apiRoot}/${resource}`);
        url.search = toSearchParams(where, { page, pageSize }).toString();

        return fetch(url.toString(), {
          method: 'GET',
          headers: headers()
        }).then(res => {
          if (!res.ok) {
            throw new Error(`Failed to fetch ${resource} page ${page}`);
          }
          return res.json();
        });
      },
      enabled,
      staleTime: STALE_TIME,
      cacheTime: STALE_TIME * 2,
      refetchOnWindowFocus: false
    };
  }

  // Page 1 has to be resolved before we know how many more pages are needed.
  const [firstPage] = useQueries({ queries: [buildQuery(1, true)] });

  const firstResolved = firstPage.isSuccess;
  const firstResults = firstResolved ? (firstPage.data?.results || []) : [];
  const totalCount = firstResolved ? (firstPage.data?.count ?? firstResults.length) : 0;
  const firstLength = firstResults.length;

  // If the API clamped the page size, use what it actually returned for the page arithmetic.
  const effectivePageSize = firstResolved && firstLength > 0 && firstLength < pageSize && firstLength < totalCount
    ? firstLength
    : pageSize;

  const enabled = [true];
  const otherQueries = [];
  for (let n = 2; n <= maxPages; n++) {
    const pageEnabled = firstResolved && totalCount > (n - 1) * effectivePageSize;
    enabled.push(pageEnabled);
    otherQueries.push(buildQuery(n, pageEnabled));
  }

  const otherPages = useQueries({ queries: otherQueries });
  const enabledPages = [firstPage, ...otherPages].filter((page, index) => enabled[index]);

  let state = PayloadStates.RESOLVED;
  if (enabledPages.some(page => page.isLoading)) {
    state = PayloadStates.FETCHING;
  } else if (enabledPages.some(page => page.isError)) {
    state = PayloadStates.ERROR_FETCHING;
  }

  const data = enabledPages.reduce((result, page) => result.concat(page.data?.results || []), []);
  const pagesFetched = enabledPages.length;
  const truncated = firstResolved && totalCount > maxPages * effectivePageSize;
  const fetched = data.length;
  const ravenEnabled = config?.raven?.enabled;

  useEffect(() => {
    if (!ravenEnabled || state !== PayloadStates.RESOLVED || !truncated) {
      return;
    }

    Sentry.withScope(scope => {
      scope.setLevel('warning');
      scope.setExtras({ source, totalCount, fetched, maxPages, pageSize: effectivePageSize });
      Sentry.captureMessage(`${message} (${source})`);
    });
  }, [source, message, totalCount, fetched, truncated, state, ravenEnabled]);

  return {
    state,
    data,
    meta: {
      totalCount,
      pagesFetched,
      truncated
    }
  };
}
